// Copyright (c) Docugami, Inc. All rights reserved.

import { TokenRange } from "networking/api";
import { Component, ComponentId } from "sdv/engine/Component";
import { Entity, IEntityManager } from "sdv/engine/Entity";
import { TokenRangeUtils } from "utils/TokenRangeUtils";
import { TokenRangeComponent } from "./TokenRangeComponent";
import { Chunk } from "store/dgml/dgmlTypes";

export enum ControlType {
    SmallChunk = 'SmallChunk',
    LargeChunk = 'LargeChunk',
    Table = 'Table',
    TableBody = 'TableBody',
    TableRow = 'TableRow',
    TableCell = 'TableCell',
    Outflow = 'Outflow'
}

export interface ControlComponent extends Component {
    typeId: ComponentId.Control
    id: string;
    type: ControlType;
    node: Chunk;
    parent: string;
    children: string[];
    fromTok: number;
    toTok: number;
    isRoot?: boolean;
}

export function getTokenRangeForControl(control: ControlComponent): TokenRange {
    return {
        fromTok: control.fromTok,
        toTok: control.toTok
    };
}

export function getTokenRange(entity: Entity | null | undefined): TokenRange | null {
    let tokenRange: TokenRange | null = null;
    const controlComp = entity?.getComponent<ControlComponent>(ComponentId.Control);
    if (!!controlComp) {
        tokenRange = getTokenRangeForControl(controlComp);
    }
    else {
        tokenRange = entity?.getComponent<TokenRangeComponent>(ComponentId.TokenRange)?.tokenRange ?? null;

    }

    return !tokenRange ? null : { ...tokenRange };
}

export interface RootControlComponent extends Component {
    typeId: ComponentId.RootControl
}

export function postOrderTraversal(e: Entity, entities: IEntityManager) {
    function* walker(e: Entity | undefined, index: number = 0): any {
        if (!e) {
            return;
        }

        const c = e.getComponent<ControlComponent>(ComponentId.Control);
        if (!c) {
            return;
        }

        yield* walker(entities.get(c.children[0]) ?? undefined, 0);

        yield e;

        const parentControl = entities.get(c.parent)?.getComponent<ControlComponent>(ComponentId.Control);
        yield* walker(entities.get(parentControl?.children[index + 1] ?? '') ?? undefined, index + 1);
    }

    return walker(e, 0);
}

export function preOrderTraversal(e: Entity, entities: IEntityManager) {
    function* walker(e: Entity | undefined, index: number = 0): any {
        if (!e) {
            return;
        }

        yield e;

        const c = e.getComponent<ControlComponent>(ComponentId.Control)!;
        yield* walker(entities.get(c.children[0]) ?? undefined, 0);

        const parentControl = entities.get(c.parent)?.getComponent<ControlComponent>(ComponentId.Control);
        yield* walker(entities.get(parentControl?.children[index + 1] ?? '') ?? undefined, index + 1);
    }

    return walker(e, 0);
}

export function* levelOrderTraversal(e: Entity, entities: IEntityManager, shouldTraverse: (c: Entity) => boolean = () => true) {
    const queue: Entity[] = [e];
    while (queue.length > 0) {
        const entity = queue.shift()!;
        const control = entity.getComponent<ControlComponent>(ComponentId.Control)!;
        if (shouldTraverse(entity)) {
            yield entity;
            for (const childId of control.children) {
                const childEntity = entities.get(childId);
                if (!!childEntity) {
                    queue.push(childEntity);
                }
            }
        }
    }
}

export function getEnclosingHyperlineForToken(token: number, root: Entity | null, entities: IEntityManager): Entity | null {
    if (!!root) {
        const iter = postOrderTraversal(root, entities);
        let result = iter.next();
        while (!!result.value) {
            const entity: Entity = result.value;
            if (entity.hasComponent(ComponentId.Hyperline)) {
                const tokenRange = getTokenRange(entity)!;
                if (token >= tokenRange.fromTok && token < tokenRange.toTok) {
                    return entity;
                }
            }

            result = iter.next();
        }
    }

    return null;
}

export function getEnclosingHyperlineForRange(range: TokenRange, root: Entity | null, entities: IEntityManager): Entity | null {
    if (!!root) {
        const iter = postOrderTraversal(root, entities);
        let result = iter.next();
        while (!!result.value) {
            const entity: Entity = result.value;
            if (entity.hasComponent(ComponentId.Hyperline)) {
                const tokenRange = getTokenRange(entity)!;
                if (TokenRangeUtils.rangeIsContained(range, tokenRange)) {
                    return entity;
                }
            }

            result = iter.next();
        }
    }

    return null;
}

export function getParentTable(entity: Entity | null, entities: IEntityManager): Entity | null {
    let curEntity = entity;
    let currentControlComp = curEntity?.getComponent<ControlComponent>(ComponentId.Control);
    while (!!currentControlComp) {
        if (currentControlComp.type === ControlType.Table) {
            return curEntity;
        }

        curEntity = entities.get(currentControlComp.parent);
        currentControlComp = curEntity?.getComponent<ControlComponent>(ComponentId.Control);
    }

    return null;
}
